/**
 * StatesSection - ordered list of `WorkflowState` rows for the
 * workflow's current version. Reads from the workflow detail, writes
 * through the state CRUD hooks. Opens `<StateDialog />` for create +
 * edit, and guards delete with a confirm.
 *
 * Reorder is handled by a simple "Move up / Move down" row action -
 * batch update via `useReorderStates` so drag-and-drop can land later
 * without changing the wire payload.
 */

import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import {
  useDeleteState,
  useReorderStates,
  type WorkflowDefinitionAdmin,
  type WorkflowStateAdmin,
  type WorkflowVersionAdmin,
} from '@/admin/hooks/use-workflow-definitions'
import { toastApiError } from '@/shared/lib/api-error'

import { StateDialog } from './state-dialog'

interface StatesSectionProps {
  workflow: WorkflowDefinitionAdmin
  version: WorkflowVersionAdmin
}

export function StatesSection({ workflow, version }: StatesSectionProps) {
  const [editing, setEditing] = useState<WorkflowStateAdmin | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [deleting, setDeleting] = useState<WorkflowStateAdmin | null>(null)
  const remove = useDeleteState(workflow.uuid)
  const reorder = useReorderStates(workflow.uuid)

  const states = useMemo(
    () => [...version.states].sort((a, b) => a.order - b.order),
    [version.states],
  )

  async function move(idx: number, direction: -1 | 1) {
    const target = idx + direction
    if (target < 0 || target >= states.length) return
    const next = [...states]
    ;[next[idx], next[target]] = [next[target], next[idx]]
    const entries = next.map((s, i) => ({ uuid: s.uuid, order: i }))
    try {
      await reorder.mutateAsync(entries)
    } catch (err) {
      toastApiError(err, 'Failed to reorder states')
    }
  }

  async function handleDelete() {
    if (!deleting) return
    try {
      await remove.mutateAsync(deleting.uuid)
      toast.success('State deleted')
      setDeleting(null)
    } catch (err) {
      toastApiError(err, 'Failed to delete state')
    }
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
          States
        </h3>
        <Button
          variant="outline"
          size="xs"
          onClick={() => setShowCreate(true)}
          data-testid="workflows-states-add"
        >
          <Plus size={11} className="mr-1" />
          Add state
        </Button>
      </div>

      {states.length === 0 ? (
        <div className="rounded-[6px] border-[0.5px] border-dashed border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
          No states yet. Add one to define where instances can rest.
        </div>
      ) : (
        <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
          {states.map((state, idx) => (
            <StateRow
              key={state.uuid}
              state={state}
              index={idx}
              total={states.length}
              onEdit={() => setEditing(state)}
              onDelete={() => setDeleting(state)}
              onMove={(dir) => move(idx, dir)}
            />
          ))}
        </div>
      )}

      <StateDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        mode="create"
        workflowUuid={workflow.uuid}
        workflowKey={workflow.key}
        resourceType={workflow.resource_type}
        allStates={states}
        defaultOrder={states.length}
      />
      <StateDialog
        open={!!editing}
        onOpenChange={(next) => {
          if (!next) setEditing(null)
        }}
        mode="edit"
        workflowUuid={workflow.uuid}
        workflowKey={workflow.key}
        resourceType={workflow.resource_type}
        allStates={states}
        state={editing ?? undefined}
      />
      <ConfirmationDialog
        open={!!deleting}
        onOpenChange={(next) => {
          if (!next) setDeleting(null)
        }}
        title={`Delete ${deleting?.label ?? 'state'}?`}
        description="This removes the state from the workflow version. Actions, rules, and running instances that reference it may need to be reviewed."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={remove.isPending}
        onConfirm={handleDelete}
      />
    </section>
  )
}

interface StateRowProps {
  state: WorkflowStateAdmin
  index: number
  total: number
  onEdit: () => void
  onDelete: () => void
  onMove: (dir: -1 | 1) => void
}

function StateRow({
  state,
  index,
  total,
  onEdit,
  onDelete,
  onMove,
}: StateRowProps) {
  return (
    <div
      className="flex items-center gap-2 border-t-[0.5px] border-t-[var(--rule)] bg-[var(--bg)] px-3 py-2 first:border-t-0"
      data-testid={`workflows-state-row-${state.uuid}`}
    >
      <ColorDot color={state.color} />
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="truncate text-[12px] text-[var(--ink)]">
          {state.label}
        </span>
        <Badge variant="outline" className="font-mono text-[10px]">
          {state.key}
        </Badge>
        {state.is_initial && (
          <Badge
            variant="secondary"
            className="text-[10px] uppercase tracking-[0.08em]"
          >
            initial
          </Badge>
        )}
        {state.is_terminal && (
          <Badge
            variant="secondary"
            className="text-[10px] uppercase tracking-[0.08em]"
          >
            terminal
          </Badge>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => onMove(-1)}
          disabled={index === 0}
          title="Move up"
        >
          <ArrowUp size={13} />
        </Button>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={() => onMove(1)}
          disabled={index === total - 1}
          title="Move down"
        >
          <ArrowDown size={13} />
        </Button>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={onEdit}
          title="Edit state"
        >
          <Pencil size={13} />
        </Button>
        <Button
          variant="ghost"
          size="icon-xs"
          onClick={onDelete}
          title="Delete state"
        >
          <Trash2 size={13} />
        </Button>
      </div>
    </div>
  )
}

function ColorDot({ color }: { color: string }) {
  return (
    <span
      className="inline-block h-3 w-3 shrink-0 rounded-full border-[0.5px] border-[var(--rule-strong)]"
      style={{ background: color || 'var(--bg-subtle)' }}
    />
  )
}
